import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, Mail, Trash2, Briefcase, Stethoscope, Landmark } from "lucide-react";
import { API } from "../../apiBase";

const TYPES = {
  ordre: {
    label: "Déclaration télésoin à l'Ordre",
    icon: Landmark,
    desc: "Document officiel attestant que vous exercez le télésoin via MetaConnect (HDS, référentiel socle), à transmettre à votre conseil de l'Ordre.",
    fields: [
      ["praticien_nom", "Nom et prénom du praticien"],
      ["profession", "Profession / spécialité"],
      ["rpps", "N° RPPS / ADELI"],
      ["adresse_cabinet", "Adresse d'exercice"],
      ["ordre_destinataire", "Conseil de l'Ordre destinataire (ex : CDOM du Rhône)"],
      ["date_debut", "Date de début d'activité (JJ/MM/AAAA)"],
    ],
  },
  travail: {
    label: "Contrat de travail (salarié)",
    icon: Briefcase,
    desc: "CDI ou CDD pour vos secrétaires, infirmiers et autres salariés, avec clauses télésanté, secret professionnel et RGPD.",
    fields: [
      ["employeur_nom", "Employeur (nom, prénom)"],
      ["structure_nom", "Nom de la structure"],
      ["siret", "SIRET / FINESS"],
      ["employeur_adresse", "Adresse de la structure"],
      ["salarie_nom", "Salarié(e) — nom, prénom"],
      ["salarie_adresse", "Adresse du salarié"],
      ["poste", "Poste (ex : secrétaire médicale, IDE)"],
      ["type_contrat", "Type : CDI ou CDD"],
      ["date_debut", "Date de début (JJ/MM/AAAA)"],
      ["date_fin", "Date de fin (CDD uniquement)"],
      ["duree_hebdo", "Durée hebdomadaire (heures)"],
      ["remuneration", "Rémunération mensuelle brute (€)"],
    ],
  },
  collaboration: {
    label: "Contrat de collaboration (confrère)",
    icon: Stethoscope,
    desc: "Collaboration libérale avec un confrère ou une consœur exerçant sur votre plateforme, avec redevance et rappel des quotas télésanté.",
    fields: [
      ["titulaire_nom", "Praticien titulaire (nom, prénom)"],
      ["titulaire_rpps", "RPPS/ADELI du titulaire"],
      ["collab_nom", "Collaborateur(trice) — nom, prénom"],
      ["collab_profession", "Profession du collaborateur"],
      ["collab_rpps", "RPPS/ADELI du collaborateur"],
      ["date_debut", "Date de début (JJ/MM/AAAA)"],
      ["jours_exercice", "Jours d'exercice (ex : lundi, jeudi)"],
      ["redevance_pct", "Redevance (% des honoraires)"],
    ],
  },
};

function ContractForm({ type, onCreated, onCancel }) {
  const cfg = TYPES[type];
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pro/telesoin/contracts`, { type, fields: values });
      toast.success("Contrat généré — vous pouvez le télécharger ou l'envoyer par email.");
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-[#BAE6FD] bg-[#F0F9FF] p-4" data-testid={`contract-form-${type}`}>
      <div className="text-[13px] font-semibold text-[#0F2B5B] mb-3">{cfg.label}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {cfg.fields.map(([key, label]) => (
          <label key={key} className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.14em] text-[#475569] mb-1">{label}</span>
            <input value={values[key] || ""} data-testid={`contract-field-${key}`}
                   onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                   className="w-full h-9 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[13px] text-[#0F2B5B] outline-none focus:border-[#0EA5E9]" />
          </label>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button onClick={submit} disabled={busy} data-testid="contract-submit"
                className="btn-primary px-5 py-2 rounded-full text-[13px] font-medium disabled:opacity-50">
          {busy ? "Génération…" : "Générer le contrat"}
        </button>
        <button onClick={onCancel} className="text-[13px] text-[#475569] hover:text-[#0F2B5B]">Annuler</button>
      </div>
    </div>
  );
}

function ContractRow({ c, onDeleted }) {
  const cfg = TYPES[c.type] || { label: c.type };
  const [email, setEmail] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    try {
      const r = await axios.get(`${API}/pro/telesoin/contracts/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.type}-metaconnect.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible.");
    }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/pro/telesoin/contracts/${c.id}/email`, { to: email.trim() });
      toast.success(`Contrat envoyé à ${email.trim()}.`);
      setShowEmail(false);
      setEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/pro/telesoin/contracts/${c.id}`);
      onDeleted(c.id);
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid={`contract-row-${c.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <FileText size={16} className="text-[#0284C7] shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <div className="text-[13.5px] font-medium text-[#0F2B5B]">{cfg.label}</div>
          <div className="text-[11px] text-[#475569]">
            Créé le {new Date(c.created_at).toLocaleDateString("fr-FR")}
            {c.emails?.length > 0 && ` · envoyé ${c.emails.length}×`}
          </div>
        </div>
        <button onClick={download} data-testid={`contract-download-${c.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E2E8F0] text-[12px] text-[#0F2B5B] hover:border-[#0EA5E9]">
          <Download size={12} /> PDF
        </button>
        <button onClick={() => setShowEmail((v) => !v)} data-testid={`contract-email-toggle-${c.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E2E8F0] text-[12px] text-[#0F2B5B] hover:border-[#0EA5E9]">
          <Mail size={12} /> Email
        </button>
        <button onClick={remove} aria-label="Supprimer" data-testid={`contract-delete-${c.id}`}
                className="grid place-items-center h-8 w-8 rounded-full text-[#DC2626] hover:bg-[#FEF2F2]">
          <Trash2 size={13} />
        </button>
      </div>
      {showEmail && (
        <div className="mt-3 flex items-center gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="destinataire@exemple.fr"
                 type="email" data-testid={`contract-email-input-${c.id}`}
                 className="flex-1 h-9 rounded-lg border border-[#E2E8F0] px-3 text-[13px] text-[#0F2B5B] outline-none focus:border-[#0EA5E9]" />
          <button onClick={sendEmail} disabled={busy || !email.includes("@")} data-testid={`contract-email-send-${c.id}`}
                  className="h-9 px-4 rounded-lg bg-[#0F2B5B] text-white text-[12.5px] disabled:opacity-40">
            {busy ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ContractsSection({ contracts, setContracts }) {
  const [openForm, setOpenForm] = useState(null);

  return (
    <div data-testid="contracts-section">
      <div className="grid sm:grid-cols-3 gap-3">
        {Object.entries(TYPES).map(([type, cfg]) => (
          <button key={type} onClick={() => setOpenForm(openForm === type ? null : type)}
                  data-testid={`contract-type-${type}`}
                  className={`text-left rounded-xl border p-4 transition ${
                    openForm === type ? "border-[#0EA5E9] bg-[#F0F9FF]" : "border-[#E2E8F0] bg-white hover:border-[#0EA5E9]"}`}>
            <cfg.icon size={18} className="text-[#0284C7]" />
            <div className="mt-2 text-[13.5px] font-semibold text-[#0F2B5B]">{cfg.label}</div>
            <p className="mt-1 text-[11.5px] text-[#475569] leading-relaxed">{cfg.desc}</p>
          </button>
        ))}
      </div>

      {openForm && (
        <ContractForm type={openForm}
                      onCreated={(c) => { setContracts((prev) => [c, ...prev]); setOpenForm(null); }}
                      onCancel={() => setOpenForm(null)} />
      )}

      {contracts.length > 0 && (
        <div className="mt-5 space-y-2.5" data-testid="contracts-list">
          {contracts.map((c) => (
            <ContractRow key={c.id} c={c}
                         onDeleted={(id) => setContracts((prev) => prev.filter((x) => x.id !== id))} />
          ))}
        </div>
      )}
    </div>
  );
}
